import { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  Image,
  Button,
  Text,
  SimpleGrid,
  Container,
  HStack,
  Icon,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Center,
} from '@chakra-ui/react';
import { useToast } from '@chakra-ui/toast';
import { FaTrophy, FaSmile, FaSadTear } from 'react-icons/fa';
import { BiTime } from 'react-icons/bi';
import { projects, Project, getRandomProjects } from '../data/projects';

interface GameSettings {
  timerDuration: number;
  numberOfQuestions: number;
}

interface RankingEntry {
  username: string;
  score: number;
  totalTime: number;
  questionsTotal: number;
}

const MIN_HEIGHT = '500px';

const GameOverScreen = ({ score, totalQuestions, onPlayAgain, username, totalTime }: { 
  score: number; 
  totalQuestions: number; 
  onPlayAgain: () => void;
  username: string;
  totalTime: number;
}) => {
  const percentage = (score / totalQuestions) * 100;
  
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };
  
  return (
    <Box p={8} w="100%" borderRadius="xl" bg="white" boxShadow="xl" minH={MIN_HEIGHT}>
      <VStack spacing={8} align="center">
        <Icon 
          as={percentage === 100 ? FaSmile : percentage < 50 ? FaSadTear : FaTrophy} 
          w={16} 
          h={16} 
          color={percentage === 100 ? "green.500" : percentage < 50 ? "red.500" : "yellow.500"}
        />
        
        <VStack spacing={2}>
          {percentage === 100 && (
            <Text fontSize="4xl" fontWeight="bold" textAlign="center" color="green.500">
              Perfect Score! 🎉
            </Text>
          )}
          
          {percentage < 50 && (
            <Text fontSize="4xl" fontWeight="bold" textAlign="center" color="red.500">
              Keep Learning! 📚
            </Text>
          )}
          
          {percentage >= 50 && percentage < 100 && (
            <Text fontSize="4xl" fontWeight="bold" textAlign="center" color="yellow.500">
              Well Done! 🌟
            </Text>
          )}
          
          <Text fontSize="xl" color="gray.600">Thanks for playing, {username}!</Text>
        </VStack>
        
        <VStack spacing={4} bg="gray.50" p={6} borderRadius="lg" w="full">
          <Text fontSize="2xl" textAlign="center">
            Your Results
          </Text>
          
          <HStack spacing={8} justify="center">
            <VStack>
              <Text color="gray.600">Score</Text>
              <Text fontSize="2xl" fontWeight="bold">
                {score}/{totalQuestions}
              </Text>
            </VStack>
            
            <VStack>
              <Text color="gray.600">Accuracy</Text>
              <Text fontSize="2xl" fontWeight="bold">
                {percentage.toFixed(1)}%
              </Text>
            </VStack>
            
            <VStack>
              <Text color="gray.600">Time</Text>
              <Text fontSize="2xl" fontWeight="bold">
                {formatTime(totalTime)}
              </Text>
            </VStack>
          </HStack>
        </VStack>
        
        {percentage === 100 && (
          <Text color="green.500" fontSize="xl" textAlign="center">
            🎉 Congratulations! You're a Cloud Native expert! Perfect score! 🎉
          </Text>
        )}
        
        {percentage < 50 && (
          <Text color="red.500" fontSize="xl" textAlign="center">
            Don't worry! Keep learning about Cloud Native projects and try again! 💪
          </Text>
        )}
        
        {percentage >= 50 && percentage < 100 && (
          <Text color="yellow.500" fontSize="xl" textAlign="center">
            Good job! You're on your way to becoming a Cloud Native expert! 🚀
          </Text>
        )}
        
        <Button 
          colorScheme={percentage === 100 ? "green" : percentage < 50 ? "red" : "yellow"} 
          onClick={onPlayAgain}
          size="lg"
          mt={4}
          width="200px"
        >
          Play Again
        </Button>
      </VStack>
    </Box>
  );
};

export const Quiz = () => {
  const [gameStarted, setGameStarted] = useState(false);
  const [currentUsername, setCurrentUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [validProjects, setValidProjects] = useState<Project[]>([]);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [timeLeft, setTimeLeft] = useState(30);
  const [totalTime, setTotalTime] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState<string | null>(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [usedProjects, setUsedProjects] = useState<Project[]>([]);
  const [showUsernameInput, setShowUsernameInput] = useState(true);
  const usernameRef = useRef<HTMLInputElement>(null);
  const [settings, setSettings] = useState<GameSettings>({
    timerDuration: 15,
    numberOfQuestions: 10,
  });
  const [tabIndex, setTabIndex] = useState(0);
  const toast = useToast();

  // Initialize ranking from localStorage
  const [ranking, setRanking] = useState<RankingEntry[]>(() => {
    try {
      const savedRanking = localStorage.getItem('quizRanking');
      return savedRanking ? JSON.parse(savedRanking) : [];
    } catch {
      return [];
    }
  });

  const generateQuestion = (projects: Project[]) => {
    // Filter out already used projects
    const availableProjects = projects.filter(p => !usedProjects.includes(p));
    
    if (availableProjects.length === 0) {
      // If we've used all projects, end the game
      setGameOver(true);
      return;
    }

    // Select a random project from available ones
    const randomProject = availableProjects[Math.floor(Math.random() * availableProjects.length)];
    setCurrentProject(randomProject);
    
    // Add the project to used projects
    setUsedProjects(prev => [...prev, randomProject]);
    
    // Generate options including the correct answer
    const otherProjects = projects
      .filter(p => p.name !== randomProject.name)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3);
    
    const allOptions = [...otherProjects.map(p => p.name), randomProject.name];
    setOptions(allOptions.sort(() => Math.random() - 0.5));
  };

  const handleAnswer = (selectedAnswer: string) => {
    if (!currentProject) return;
    
    const correct = selectedAnswer === currentProject.name;
    if (correct) {
      setScore(prev => prev + 1);
    }

    if (questionCount < settings.numberOfQuestions - 1) {
      setQuestionCount(prev => prev + 1);
      setTimeLeft(settings.timerDuration);
      generateQuestion(validProjects);
    } else {
      setGameOver(true);
    }
  };

  // Add timer effect for total time
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameStarted && !gameOver) {
      interval = setInterval(() => {
        setTotalTime(prev => prev + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [gameStarted, gameOver]);

  // Add new useEffect to handle ranking updates
  useEffect(() => {
    if (gameOver && currentUsername) {
      const newEntry: RankingEntry = {
        username: currentUsername,
        score: score,
        totalTime,
        questionsTotal: settings.numberOfQuestions
      };

      try {
        const savedRanking = localStorage.getItem('quizRanking');
        const currentRanking: RankingEntry[] = savedRanking ? JSON.parse(savedRanking) : [];

        const newRanking = [...currentRanking, newEntry]
          .sort((a, b) => {
            const aPercentage = (a.score / a.questionsTotal) * 100;
            const bPercentage = (b.score / b.questionsTotal) * 100;
            
            if (bPercentage !== aPercentage) {
              return bPercentage - aPercentage;
            }
            return a.totalTime - b.totalTime;
          })
          .slice(0, 10);

        setRanking(newRanking);
        localStorage.setItem('quizRanking', JSON.stringify(newRanking));
      } catch (error) {
        console.error('Error updating ranking:', error);
      }
    }
  }, [gameOver, currentUsername, score, totalTime, settings.numberOfQuestions]);

  const startGame = async () => {
    const username = usernameRef.current?.value || '';
    if (!username) {
      toast({
        title: 'Username Required',
        description: 'Please enter a username',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      // Get enough projects for the quiz (we need numberOfQuestions + 3 for options)
      const projectsNeeded = Math.min(settings.numberOfQuestions + 3, projects.length);
      const validatedProjects = await getRandomProjects(projectsNeeded);
      
      if (validatedProjects.length < 4) {
        toast({
          title: 'Error',
          description: 'Not enough valid projects available. Please try again later.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      setValidProjects(validatedProjects);
      setCurrentUsername(username);
      setScore(0);
      setGameOver(false);
      setGameStarted(true);
      setSelectedAnswer(null);
      setQuestionCount(0);
      setTotalTime(0);
      setUsedProjects([]); // Reset used projects
      setShowUsernameInput(false);
      setTimeLeft(settings.timerDuration);
      generateQuestion(validatedProjects);
    } catch (error) {
      console.error('Error loading projects:', error);
      toast({
        title: 'Error',
        description: 'Failed to load projects. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (timeLeft > 0 && !gameOver && gameStarted) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);

      return () => clearInterval(timer);
    } else if (timeLeft === 0 && !gameOver && gameStarted) {
      toast({
        title: 'Time\'s up!',
        description: `The correct answer was ${currentProject?.name}`,
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
      
      if (questionCount >= settings.numberOfQuestions - 1) {
        setGameOver(true);
      } else {
        setQuestionCount(prev => prev + 1);
        setTimeLeft(settings.timerDuration);
        generateQuestion(validProjects);
      }
    }
  }, [timeLeft, gameOver, currentProject?.name, gameStarted, questionCount, settings.numberOfQuestions, settings.timerDuration]);

  const getButtonColor = (option: string) => {
    return 'white';
  };

  const getButtonHoverStyle = (option: string) => {
    return { bg: 'gray.50' };
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const GameComponent = () => {
    if (loading) {
      return (
        <Box
          p={8}
          w="100%"
          borderRadius="xl"
          bg="white"
          boxShadow="xl"
          minH={MIN_HEIGHT}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <VStack spacing={4}>
            <Spinner size="xl" />
            <Text>Loading CNCF Projects...</Text>
          </VStack>
        </Box>
      );
    }

    if (!gameStarted) {
      return (
        <VStack spacing={6} align="stretch">
          <Box textAlign="center" mb={4}>
            <Text fontSize="2xl" fontWeight="bold" mb={2}>Welcome to CNCF Quiz Game!</Text>
            <Text color="gray.600" fontSize="lg">
              Test your knowledge about Cloud Native Computing Foundation projects. 
              Match project logos with their names and compete for the top spot on our leaderboard!
            </Text>
          </Box>
          <Input
            ref={usernameRef}
            placeholder="Enter your username"
            size="lg"
          />
          <Button
            colorScheme="blue"
            onClick={startGame}
            isLoading={loading}
          >
            Start Game
          </Button>
        </VStack>
      );
    }

    if (gameOver) {
      return (
        <GameOverScreen 
          score={score} 
          totalQuestions={settings.numberOfQuestions} 
          username={currentUsername}
          totalTime={totalTime}
          onPlayAgain={() => {
            setGameStarted(false);
            setShowUsernameInput(true);
            setGameOver(false);
            setScore(0);
            setQuestionCount(0);
            setTotalTime(0);
            setUsedProjects([]);
          }} 
        />
      );
    }

    return (
      <VStack gap={8} w="100%" minH={MIN_HEIGHT}>
        <Flex justify="space-between" w="100%" align="center">
          <HStack spacing={2}>
            <Icon as={FaTrophy} color="yellow.400" boxSize={8} />
            <Text fontSize="2xl" fontWeight="bold">Score: {score}</Text>
          </HStack>
          <VStack spacing={0} align="center">
            <Text fontSize="md" color="gray.600">Question</Text>
            <Text fontSize="xl" fontWeight="bold">{questionCount + 1}/{settings.numberOfQuestions}</Text>
          </VStack>
          <HStack spacing={2}>
            <Icon as={BiTime} color="blue.400" boxSize={8} />
            <Text fontSize="2xl" fontWeight="bold">{timeLeft}s</Text>
          </HStack>
        </Flex>
        
        {currentProject && (
          <Box
            p={8}
            w="100%"
            borderRadius="xl"
            bg="white"
            boxShadow="xl"
          >
            <VStack gap={8}>
              <Image
                src={currentProject.logo}
                alt="Project Logo"
                boxSize="200px"
                objectFit="contain"
              />
              <Text fontSize="md" color="gray.500">
                Category: {currentProject.category}
              </Text>
              
              <SimpleGrid columns={[1, 2]} gap={4} w="100%">
                {options.map((option, index) => (
                  <Button
                    key={index}
                    onClick={() => handleAnswer(option)}
                    size="lg"
                    height="60px"
                    bg={getButtonColor(option)}
                    color="black"
                    _hover={getButtonHoverStyle(option)}
                    fontSize="xl"
                    border="1px"
                    borderColor="gray.200"
                  >
                    {option}
                  </Button>
                ))}
              </SimpleGrid>
            </VStack>
          </Box>
        )}
      </VStack>
    );
  };

  const SettingsComponent = () => {
    const [timerDuration, setTimerDuration] = useState(settings.timerDuration);
    const [numberOfQuestions, setNumberOfQuestions] = useState(settings.numberOfQuestions);

    const handleTimerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? 5 : Number(e.target.value);
      setTimerDuration(value);
    };

    const handleQuestionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value === '' ? 5 : Number(e.target.value);
      setNumberOfQuestions(value);
    };

    const handleSave = () => {
      // Validate all settings
      if (timerDuration < 5 || timerDuration > 30) {
        toast({
          title: 'Invalid Timer Duration',
          description: 'Timer must be between 5 and 30 seconds',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (numberOfQuestions < 5 || numberOfQuestions > 20) {
        toast({
          title: 'Invalid Number of Questions',
          description: 'Number of questions must be between 5 and 20',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      setSettings({
        timerDuration,
        numberOfQuestions
      });
      setTimeLeft(timerDuration);
      toast({
        title: 'Settings saved!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      setTabIndex(0);
    };

    return (
      <Box
        p={8}
        w="100%"
        borderRadius="xl"
        bg="white"
        boxShadow="xl"
        minH={MIN_HEIGHT}
      >
        <VStack gap={6} align="stretch">
          <Text fontSize="xl" fontWeight="bold" textAlign="center">
            Game Settings
          </Text>

          <FormControl>
            <FormLabel>Timer Duration (5-30 seconds)</FormLabel>
            <input
              type="number"
              value={timerDuration}
              onChange={handleTimerChange}
              min={5}
              max={30}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                fontSize: '16px'
              }}
            />
          </FormControl>

          <FormControl>
            <FormLabel>Number of Questions (5-20)</FormLabel>
            <input
              type="number"
              value={numberOfQuestions}
              onChange={handleQuestionsChange}
              min={5}
              max={20}
              style={{
                width: '100%',
                padding: '8px',
                border: '1px solid #E2E8F0',
                borderRadius: '6px',
                fontSize: '16px'
              }}
            />
          </FormControl>

          <Button
            colorScheme="blue"
            size="lg"
            onClick={handleSave}
            mt={4}
          >
            Save Settings
          </Button>
        </VStack>
      </Box>
    );
  };

  return (
    <Flex 
      minH="100vh" 
      direction="column" 
      bg="gray.50"
    >
      <Container maxW="container.md" flex="1" py={10} display="flex" alignItems="center">
        <VStack gap={8} w="100%">
          <Tabs 
            isFitted 
            variant="enclosed" 
            w="100%" 
            index={tabIndex}
            onChange={(index) => setTabIndex(index)}
          >
            <TabList mb="1em">
              <Tab>Play</Tab>
              <Tab>Customize</Tab>
            </TabList>
            <TabPanels>
              <TabPanel p={0}>
                <GameComponent />
              </TabPanel>
              <TabPanel p={0}>
                <SettingsComponent />
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Container>
      <Box as="footer" py={4} textAlign="center" bg="white" borderTop="1px" borderColor="gray.200">
        <Text color="gray.600">
          Made with ❤️ for the Cloud Native Community
        </Text>
      </Box>
    </Flex>
  );
}; 